import React, { useContext, useEffect, useMemo } from 'react';
import cx from 'classnames';

import { Dropdown } from 'components';
import { XS } from 'components/typography';
import { useChargeCodesOptions } from 'pages/portal/admin/channel/charges-invoices/hooks/useChargeCodesOptions';
import { useTranslation } from 'react-i18next';
import { ChannelsContext } from 'lane-shared/contexts';
import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import { convertTo62 } from 'uuid-encoding';

import styles from './CreateProductForm.scss';

type AdminFeeChargeCodeDropdownProps = {
  adminFeeChargeCodeId?: string;
  updateDetails: (value: any, field: string) => void;
};

export function AdminFeeChargeCodeDropdown({
  adminFeeChargeCodeId,
  updateDetails,
}: AdminFeeChargeCodeDropdownProps) {
  const { t } = useTranslation();

  const { primaryChannel } = useContext(ChannelsContext);
  const channelId = primaryChannel?._id;

  const { adminFeeChargeCodeId: rawDefaultAdminFeeChargeCodeId } =
    useBillingPaymentSettingsForChannel({ channelId });

  const defaultAdminFeeChargeCodeId = useMemo(
    () =>
      rawDefaultAdminFeeChargeCodeId
        ? convertTo62(rawDefaultAdminFeeChargeCodeId)
        : null,
    [rawDefaultAdminFeeChargeCodeId]
  );

  const chargeCodeOptions = useChargeCodesOptions(channelId);

  useEffect(() => {
    if (defaultAdminFeeChargeCodeId && !adminFeeChargeCodeId) {
      updateDetails(defaultAdminFeeChargeCodeId, 'adminFeeChargeCodeId');
    }
  }, [defaultAdminFeeChargeCodeId, adminFeeChargeCodeId, updateDetails]);

  if (!defaultAdminFeeChargeCodeId) {
    return null;
  }

  const selectedAdminFeeChargeCodeId =
    adminFeeChargeCodeId ?? defaultAdminFeeChargeCodeId;

  return (
    <div className="mb-2">
      <div className={cx(styles.label, 'mt-2')}>
        <label className={styles.descriptionLabel}>
          {t('abp.productsServices.createProduct.adminFeeChargeCodeLabel')}
        </label>
      </div>
      <Dropdown
        testId="adminFeeChargeCodeDropdown"
        doTranslation={false}
        className={cx(
          styles.inputFields,
          styles.inputWithCustomLabel,
          styles.categoryDropdown,
          'mb-0'
        )}
        value={selectedAdminFeeChargeCodeId}
        placeholder={t(
          'abp.productsServices.createProduct.chargeCode.placeholder'
        )}
        items={chargeCodeOptions}
        onChange={(item: { value: string }) => {
          updateDetails(item.value, 'adminFeeChargeCodeId');
        }}
      />
      <XS variant="secondary">
        {t('abp.productsServices.createProduct.adminFeeChargeCode.helperText')}
      </XS>
    </div>
  );
}
